/**
 * Support — чат с поддержкой. Опрашивает ветку каждые POLL_MS (сокетов нет); отправка текста и
 * файлов через supportApi. Оператор отвечает из админки, ответ подтягивается опросом.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useBackButton } from "@/telegram/useMainButton";
import { tg } from "@/telegram/tg";
import {
  friendlyError,
  supportApi,
  type SupportAttachment,
  type SupportMessage,
} from "@/lib/api";

type Props = { onBack: () => void };

const ACCEPT = "image/*,application/pdf";
const POLL_MS = 4000;

function showAlert(message: string) {
  const wa = tg();
  if (wa?.showAlert) wa.showAlert(message);
  else window.alert(message);
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function Attachment({ att }: { att: SupportAttachment }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    supportApi
      .attachmentUrl(att.url)
      .then((u) => {
        objectUrl = u;
        if (alive) setUrl(u);
        else URL.revokeObjectURL(u);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [att.url]);

  if (failed) {
    return <div className="text-[13px] opacity-70">📎 {att.name} (не удалось загрузить)</div>;
  }

  if (att.is_image) {
    if (!url) return <div className="h-40 w-40 animate-pulse rounded-xl bg-white/10" />;
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img src={url} alt={att.name} className="max-h-60 max-w-full rounded-xl object-contain" />
      </a>
    );
  }

  return (
    <a
      href={url ?? undefined}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-[14px] text-white ${url ? "" : "opacity-50"}`}
    >
      <span className="text-lg">📄</span>
      <span className="min-w-0 flex-1 truncate">{att.name}</span>
    </a>
  );
}

function Bubble({ msg }: { msg: SupportMessage }) {
  const mine = msg.sender === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3 py-2 ${
          mine
            ? "rounded-br-md bg-brand text-surface-base"
            : "rounded-bl-md border border-white/[0.06] bg-white/[0.06] text-white"
        }`}
      >
        {msg.attachment && (
          <div className={msg.body ? "mb-1.5" : ""}>
            <Attachment att={msg.attachment} />
          </div>
        )}
        {msg.body && <div className="whitespace-pre-wrap break-words text-[15px]">{msg.body}</div>}
        <div className={`mt-0.5 text-right text-[11px] ${mine ? "text-surface-base/60" : "text-white/40"}`}>
          {timeLabel(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

export function Support({ onBack }: Props) {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useBackButton(onBack);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const t = await supportApi.thread();
      setMessages((prev) => {
        if (prev.length !== t.messages.length) scrollToBottom();
        return t.messages;
      });
    } catch {
      /* ignore — не залогинен / офлайн */
    } finally {
      setLoaded(true);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    void load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    scrollToBottom();
  }, [loaded, scrollToBottom]);

  const sendText = useCallback(async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const msg = await supportApi.send(body);
      setText("");
      setMessages((prev) => [...prev, msg]);
      scrollToBottom();
    } catch (e) {
      showAlert(friendlyError(e));
    } finally {
      setSending(false);
    }
  }, [text, sending, scrollToBottom]);

  const onPickFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || sending) return;
      setSending(true);
      try {
        const msg = await supportApi.sendFile(file);
        setMessages((prev) => [...prev, msg]);
        scrollToBottom();
      } catch (err) {
        showAlert(friendlyError(err, "Не удалось отправить файл."));
      } finally {
        setSending(false);
      }
    },
    [sending, scrollToBottom],
  );

  const insideTg = typeof window !== "undefined" && !!tg()?.initData;

  return (
    <div className="flex min-h-dvh flex-col bg-surface-base text-white">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-white/[0.06] bg-surface-base/80 px-4 py-3 backdrop-blur-xl">
        {!insideTg && (
          <button onClick={onBack} className="text-[15px] text-white/60 active:opacity-60" aria-label="Назад">
            ‹
          </button>
        )}
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[18px] shadow-lg shadow-brand/20">
          🎧
        </div>
        <div className="min-w-0">
          <div className="text-[16px] font-semibold">Поддержка</div>
          <div className="text-[12px] text-white/45">Обычно отвечаем в течение нескольких минут</div>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {loaded && messages.length === 0 && (
          <div className="mt-16 flex flex-col items-center px-6 text-center">
            <div className="mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-white/[0.06] text-[26px]">💬</div>
            <div className="text-[15px] font-medium">Чем помочь?</div>
            <div className="mt-1 text-[13px] leading-snug text-white/45">
              Задайте вопрос — оператор ответит здесь. Можно прикрепить фото или PDF.
            </div>
          </div>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} msg={m} />
        ))}
      </div>

      <div className="sticky bottom-0 flex items-end gap-2 border-t border-white/[0.06] bg-surface-base/80 px-3 py-2.5 pb-[calc(env(safe-area-inset-bottom)+0.6rem)] backdrop-blur-xl">
        <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={onPickFile} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={sending}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-[20px] text-white/50 active:bg-white/5 disabled:opacity-40"
          aria-label="Прикрепить файл"
        >
          📎
        </button>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void sendText();
            }
          }}
          rows={1}
          placeholder="Сообщение…"
          className="max-h-32 min-h-[40px] flex-1 resize-none rounded-2xl border border-white/[0.08] bg-white/[0.06] px-3.5 py-2.5 text-[15px] text-white outline-none placeholder:text-white/35 focus:border-white/20"
        />
        <button
          onClick={() => void sendText()}
          disabled={sending || !text.trim()}
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand text-surface-base transition active:brightness-95 disabled:opacity-40"
          aria-label="Отправить"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <path d="M12 20V5" />
            <path d="m5 12 7-7 7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}
